import { React, useContext, useState, useEffect } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import {
    Autocomplete, Box, Button, FormControl, FormControlLabel,
    Grid, IconButton, InputLabel, MenuItem,
    Paper, Select, Switch, TextField, Tooltip, Typography
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { DataGrid } from '@mui/x-data-grid';
import { useLocation } from 'react-router-dom';

import PageHeader from '../../components/pageHeader';
import { Alerta } from '../../components/alerta';
import SubmitButton from '../../components/submitButton';
import DadosUsuario from '../../utils/context';
import { fetchFlask } from '../../utils/fetchFlask';
import { maskCEP, maskCpfCnpj, maskPhone } from '../../utils/maskCpfCnpj';
import { getDigits } from '../../utils/stringFormat';

const CREATE_DOCUMENT_UNIT_PDF_API = 'crm/insertLeadUnits';
const manualAddressFields = ['rua', 'numero', 'complemento', 'bairro', 'cidade', 'estado', 'cep'];
const requiredManualUnitFields = ['titular', 'codigo_do_cliente', 'instalacao', 'consumo_medio'];
const requiredManualAddressFields = ['rua', 'numero', 'bairro', 'cidade', 'estado', 'cep'];
const ESTADOS_ATENDIDOS = ['PE', 'RN'];
const ESTADOS_BRASIL = [
    ...ESTADOS_ATENDIDOS,
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA',
    'PB', 'PR', 'PI', 'RJ', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];
// Cupons que só podem ser usados quando já vêm do lead no CRM
const CUPONS_RESTRITOS_CRM = ['1REMARKETING'];

const normalizeCoupon = (coupon) => String(getFilledValue(coupon)).trim().toUpperCase();

const createManualUnitForm = () => ({
    titular: '',
    codigo_do_cliente: '',
    instalacao: '',
    consumo_medio: '',
    alta_tensao: false,
    endereco: manualAddressFields.reduce((acc, field) => ({ ...acc, [field]: '' }), {})
});

const getFilledValue = (value, fallback = '') => {
    if (value === null || value === undefined) return fallback;
    return value;
};

const getClientTypeFromDocument = (document) => (
    getDigits(document).length > 11 ? 'pj' : 'pf'
);

const getMaskedCpfCnpj = (document) => maskCpfCnpj(getFilledValue(document));

const getMaskedPhone = (phone) => maskPhone(getFilledValue(phone));

const getFirstName = (name) => getFilledValue(name).trim().split(/\s+/)[0] ?? '';

const getFirstUnitCep = (crmLeadUnits = []) => {
    const firstUnit = Array.isArray(crmLeadUnits) ? crmLeadUnits[0] : null;
    return firstUnit?.endereco?.cep ?? '';
};

const formatCrmUnitAddress = (address) => {
    if (!address) return '';

    return [
        address.rua,
        address.numero,
        address.complemento,
        address.bairro,
        address.cidade,
        address.estado,
        address.cep
    ].filter(Boolean).join(' ');
};

const getDistribuidoraFromState = (state) => state === 'RN' ? 'COSERN' : state === 'PE' ? 'NEOENERGIA PE' : '';

const mapUnitToRow = (unit, id) => ({
    id,
    cc: getFilledValue(unit.codigo_do_cliente ?? unit.cc),
    address: getFilledValue(unit.address, formatCrmUnitAddress(unit.endereco)),
    consumption: getFilledValue(unit.consumo_medio ?? unit.consumption),
    state: getFilledValue(unit.state, getDistribuidoraFromState(unit.endereco?.estado))
});

const mapCrmUnitsToRows = (crmLeadUnits = []) => crmLeadUnits.map((unit, index) => mapUnitToRow(unit, index + 1));

const mapManualUnitToRow = (manualUnitForm, id) => mapUnitToRow({
    codigo_do_cliente: manualUnitForm.codigo_do_cliente,
    consumo_medio: manualUnitForm.consumo_medio,
    endereco: manualUnitForm.endereco
}, id);

const buildManualUnitPayload = (manualUnitForm) => ({
    titular: manualUnitForm.titular?.trim() ?? '',
    codigo_do_cliente: manualUnitForm.codigo_do_cliente?.trim() ?? '',
    instalacao: manualUnitForm.instalacao?.trim() ?? '',
    consumo_medio: manualUnitForm.consumo_medio?.trim() ?? '',
    alta_tensao: Boolean(manualUnitForm.alta_tensao),
    endereco: manualAddressFields.reduce((acc, field) => {
        acc[field] = manualUnitForm.endereco?.[field]?.trim() ?? '';
        return acc;
    }, {})
});

const extractUnitsFromInsertResponse = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.units)) return response.units;
    if (response?.data && typeof response.data === 'object') return [response.data];
    return [];
};

const createDocuments = () => {
    const location = useLocation();
    const crmLeadData = location.state?.leadData;
    const crmLeadUnits = location.state?.leadUnits;
    const crmClientType = crmLeadData?.cpf ? getClientTypeFromDocument(crmLeadData.cpf) : 'pj';

    const [approved, setApproved] = useState(crmLeadData?.cpf);
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [clientType, setClientType] = useState(crmClientType);
    const [clientName, setClientName] = useState(() => getFilledValue(crmLeadData?.nome));
    const [cpfCnpj, setCpfCnpj] = useState(() => getMaskedCpfCnpj(crmLeadData?.cpf));
    const [coupon, setCoupon] = useState(() => getFilledValue(crmLeadData?.cupom));
    const [couponsList, setCouponsList] = useState([]);
    const crmLeadCoupon = normalizeCoupon(crmLeadData?.cupom);
    const selectableCoupons = couponsList.filter((item) => {
        const normalized = normalizeCoupon(item);
        return !CUPONS_RESTRITOS_CRM.includes(normalized) || normalized === crmLeadCoupon;
    });
    const [maritalStatus, setMaritalStatus] = useState('');
    const [street, setStreet] = useState('');
    const [number, setNumber] = useState('');
    const [complement, setComplement] = useState('');
    const [neighborhood, setNeighborhood] = useState('');
    const [city, setCity] = useState('');
    const [cep, setCep] = useState(() => crmClientType === 'pf' ? maskCEP(getFirstUnitCep(crmLeadUnits)) : '');
    const [representativeName, setRepresentativeName] = useState('');
    const [representativeCpf, setRepresentativeCpf] = useState('');
    const [representativeAddress, setRepresentativeAddress] = useState('');
    const [representativeCep, setRepresentativeCep] = useState('');
    const discount = (() => {
        const leadDiscount = crmLeadData?.desconto;
        return leadDiscount === null || leadDiscount === undefined || leadDiscount === ''
            ? 20
            : Math.round(Number(leadDiscount) * 100);
    })();
    const [contactEmail, setContactEmail] = useState(() => getFilledValue(crmLeadData?.email));
    const [contactName, setContactName] = useState(() => getFirstName(crmLeadData?.nome));
    const [phone1, setPhone1] = useState(() => getMaskedPhone(crmLeadData?.telefone));
    const [phone2, setPhone2] = useState('');
    const [state, setState] = useState('');
    const [classification, setClassification] = useState('');
    const [verificationType, setVerificationType] = useState('');
    const [rows, setRows] = useState(() => mapCrmUnitsToRows(crmLeadUnits));
    const [selectedRows, setSelectedRows] = useState([]);
    const [unitPdfFile, setUnitPdfFile] = useState(null);
    const [isInsertingUnit, setIsInsertingUnit] = useState(false);
    const [showManualUnitForm, setShowManualUnitForm] = useState(false);
    const [manualUnitForm, setManualUnitForm] = useState(createManualUnitForm);
    const [files, setFiles] = useState(
        { cnh: null, contrato: null, cnpj: null, neoenergiaBill: null }
    );
    const [oppName, setOppName] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);
    const [notificarCliente, setNotificarCliente] = useState(true);

    const dadosUsuario = useContext(DadosUsuario);
    const emailPortal = dadosUsuario.email === 'maurofarias@settaenergia.com.br';
    const canChooseClientNotification = emailPortal || dadosUsuario.autorizacoes?.includes('Adm');

    const cidadesPe = [
        'Abreu e Lima', 'Afogados da Ingazeira', 'Afrânio', 'Agrestina',
        'Água Preta', 'Águas Belas', 'Alagoinha', 'Aliança', 'Altinho',
        'Amaraji', 'Angelim', 'Araçoiaba', 'Araripina', 'Arcoverde',
        'Barra de Guabiraba', 'Barreiros', 'Belém de Maria', 'Belém de São Francisco',
        'Belo Jardim', 'Betânia', 'Bezerros', 'Bodocó', 'Bom Conselho',
        'Bom Jardim', 'Bonito', 'Brejão', 'Brejinho', 'Brejo da Madre de Deus',
        'Buenos Aires', 'Buíque', 'Cabo de Santo Agostinho', 'Cabrobó',
        'Cachoeirinha', 'Caetés', 'Calçado', 'Calumbi', 'Camaragibe',
        'Camocim de São Félix', 'Camutanga', 'Canhotinho', 'Capoeiras',
        'Carnaíba', 'Carnaubeira da Penha', 'Carpina', 'Caruaru',
        'Casinhas', 'Catende', 'Cedro', 'Chã de Alegria', 'Chã Grande',
        'Condado', 'Correntes', 'Cortês', 'Cumaru', 'Cupira',
        'Custódia', 'Dormentes', 'Escada', 'Exu', 'Feira Nova',
        'Fernando de Noronha', 'Ferreiros', 'Flores', 'Floresta',
        'Frei Miguelinho', 'Gameleira', 'Garanhuns', 'Glória do Goitá',
        'Goiana', 'Granito', 'Gravatá', 'Iati', 'Ibimirim',
        'Ibirajuba', 'Igarassu', 'Iguaracy', 'Ilha de Itamaracá',
        'Inajá', 'Ingazeira', 'Ipojuca', 'Ipubi', 'Itacuruba',
        'Itaíba', 'Itambé', 'Itapetim', 'Itapissuma', 'Itaquitinga',
        'Jaboatão dos Guararapes', 'Jaqueira', 'Jataúba', 'Jatobá',
        'João Alfredo', 'Joaquim Nabuco', 'Jucati', 'Jupi',
        'Jurema', 'Lagoa de Itaenga', 'Lagoa do Carro', 'Lagoa do Ouro',
        'Lagoa dos Gatos', 'Lagoa Grande', 'Lajedo', 'Limoeiro',
        'Macaparana', 'Machados', 'Manari', 'Maraial', 'Mirandiba',
        'Moreilândia', 'Moreno', 'Nazaré da Mata', 'Olinda',
        'Orobó', 'Orocó', 'Ouricuri', 'Palmares', 'Palmeirina',
        'Panelas', 'Paranatama', 'Parnamirim', 'Passira', 'Paudalho',
        'Paulista', 'Pedra', 'Pesqueira', 'Petrolândia', 'Petrolina',
        'Poção', 'Pombos', 'Primavera', 'Quipapá', 'Quixaba',
        'Recife', 'Riacho das Almas', 'Ribeirão', 'Rio Formoso',
        'Sairé', 'Salgadinho', 'Salgueiro', 'Saloá', 'Sanharó',
        'Santa Cruz', 'Santa Cruz da Baixa Verde', 'Santa Cruz do Capibaribe',
        'Santa Filomena', 'Santa Maria da Boa Vista', 'Santa Maria do Cambucá',
        'Santa Terezinha', 'São Benedito do Sul', 'São Bento do Una',
        'São Caitano', 'São João', 'São Joaquim do Monte', 'São José da Coroa Grande',
        'São José do Belmonte', 'São José do Egito', 'São Lourenço da Mata',
        'São Vicente Férrer', 'Serra Talhada', 'Serrita', 'Sertânia',
        'Sirinhaém', 'Solidão', 'Surubim', 'Tabira', 'Tacaimbó',
        'Tacaratu', 'Tamandaré', 'Taquaritinga do Norte', 'Terezinha',
        'Terra Nova', 'Timbaúba', 'Toritama', 'Tracunhaém',
        'Trindade', 'Triunfo', 'Tupanatinga', 'Tuparetama',
        'Venturosa', 'Verdejante', 'Vertente do Lério', 'Vertentes',
        'Vicência', 'Vitória de Santo Antão', 'Xexéu'
    ];

    const cidadesRn = [
        'Acari', 'Açu', 'Afonso Bezerra', 'Água Nova', 'Alexandria',
        'Almino Afonso', 'Alto do Rodrigues', 'Angicos', 'Antônio Martins',
        'Apodi', 'Areia Branca', 'Arês', 'Augusto Severo',
        'Baía Formosa', 'Baraúna', 'Barcelona', 'Bento Fernandes',
        'Bodó', 'Bom Jesus', 'Brejinho', 'Caiçara do Norte',
        'Caiçara do Rio do Vento', 'Caicó', 'Campo Redondo',
        'Canguaretama', 'Caraúbas', 'Carnaúba dos Dantas',
        'Carnaubais', 'Ceará-Mirim', 'Cerro Corá',
        'Coronel Ezequiel', 'Coronel João Pessoa',
        'Cruzeta', 'Currais Novos', 'Doutor Severiano',
        'Encanto', 'Equador', 'Espírito Santo',
        'Extremoz', 'Felipe Guerra', 'Fernando Pedroza',
        'Florânia', 'Francisco Dantas', 'Frutuoso Gomes',
        'Galinhos', 'Goianinha', 'Governador Dix-Sept Rosado',
        'Grossos', 'Guamaré', 'Ielmo Marinho',
        'Ipanguaçu', 'Ipueira', 'Itajá', 'Itaú',
        'Jaçanã', 'Jandaíra', 'Janduís', 'Januário Cicco',
        'Japi', 'Jardim de Angicos', 'Jardim de Piranhas',
        'Jardim do Seridó', 'João Câmara', 'João Dias',
        'José da Penha', 'Jucurutu', 'Jundiá',
        'Lagoa d\'Anta', 'Lagoa de Pedras', 'Lagoa de Velhos',
        'Lagoa Nova', 'Lagoa Salgada', 'Lajes',
        'Lajes Pintadas', 'Lucrécia', 'Luís Gomes',
        'Macaíba', 'Macau', 'Major Sales',
        'Marcelino Vieira', 'Martins', 'Maxaranguape',
        'Messias Targino', 'Montanhas', 'Monte Alegre',
        'Monte das Gameleiras', 'Mossoró', 'Natal',
        'Nísia Floresta', 'Nova Cruz', 'Olho d\'Água do Borges',
        'Ouro Branco', 'Paraná', 'Paraú',
        'Parazinho', 'Parelhas', 'Parnamirim',
        'Passa e Fica', 'Passagem', 'Patu',
        'Pau dos Ferros', 'Pedra Grande', 'Pedra Preta',
        'Pedro Avelino', 'Pedro Velho', 'Pendências',
        'Pilões', 'Poço Branco', 'Portalegre',
        'Porto do Mangue', 'Presidente Juscelino',
        'Pureza', 'Rafael Fernandes', 'Rafael Godeiro',
        'Riacho da Cruz', 'Riacho de Santana',
        'Riachuelo', 'Rio do Fogo', 'Rodolfo Fernandes',
        'Ruy Barbosa', 'Santa Cruz', 'Santa Maria',
        'Santana do Matos', 'Santana do Seridó',
        'Santo Antônio', 'São Bento do Norte',
        'São Bento do Trairi', 'São Fernando',
        'São Francisco do Oeste', 'São Gonçalo do Amarante',
        'São João do Sabugi', 'São José de Mipibu',
        'São José do Campestre', 'São José do Seridó',
        'São Miguel', 'São Miguel do Gostoso',
        'São Paulo do Potengi', 'São Pedro',
        'São Rafael', 'São Tomé',
        'São Vicente', 'Senador Elói de Souza',
        'Senador Georgino Avelino', 'Serra de São Bento',
        'Serra do Mel', 'Serra Negra do Norte',
        'Serrinha', 'Serrinha dos Pintos',
        'Severiano Melo', 'Sítio Novo',
        'Taboleiro Grande', 'Taipu',
        'Tangará', 'Tenente Ananias',
        'Tenente Laurentino Cruz', 'Tibau',
        'Tibau do Sul', 'Timbaúba dos Batistas',
        'Touros', 'Triunfo Potiguar',
        'Umarizal', 'Upanema',
        'Várzea', 'Venha-Ver',
        'Vera Cruz', 'Viçosa',
        'Vila Flor'
    ];

    const cidadesPorEstado = {
        PE: cidadesPe,
        RN: cidadesRn
    };

    const columns = [
        {
            field: 'cc',
            headerName: 'CC',
            width: 150,
            editable: true
        },
        {
            field: 'address',
            headerName: 'Endereço',
            width: 150,
            editable: true
        },
        {
            field: 'consumption',
            headerName: 'Consumo',
            type: 'number',
            width: 100,
            editable: true
        },
        {
            field: 'state',
            headerName: 'Estado',
            width: 150,
            editable: false
        }
    ];

    const handleRowSelection = (newSelection) => {
        setSelectedRows(newSelection);
    };

    const deleteCC = (id) => {
        const ids = Array.isArray(id) ? id : [id];
        setRows((prevRows) => prevRows.filter((row) => !ids.includes(row.id)));
    };

    const processRowUpdate = (newRow) => {
        setRows((prevRows) =>
            prevRows.map((row) => (row.id === newRow.id ? newRow : row))
        );
        return newRow;
    };

    const updateManualUnitField = (field, value) => {
        setManualUnitForm((prev) => ({ ...prev, [field]: value }));
    };

    const updateManualUnitAddressField = (field, value) => {
        setManualUnitForm((prev) => ({
            ...prev,
            endereco: {
                ...(prev.endereco ?? {}),
                [field]: value
            }
        }));
    };

    const insertUnitByPdf = async () => {
        if (!unitPdfFile) {
            setMessage('Envie um PDF para inserir a unidade.');
            setStatus('error');
            setOpenAlerta(true);
            return;
        }

        setIsInsertingUnit(true);
        const formData = new FormData();
        formData.append('contaEnergia', unitPdfFile);
        formData.append('nome', clientName);
        formData.append('cpf', cpfCnpj);
        if (crmLeadData?.lead_id) {
            formData.append('lead_id', String(crmLeadData.lead_id));
        }

        const response = await fetchFlask(CREATE_DOCUMENT_UNIT_PDF_API, formData);
        const returnedUnits = extractUnitsFromInsertResponse(response);

        if (response?.status === 'success' && returnedUnits.length > 0) {
            setRows((prevRows) => {
                const nextId = prevRows.reduce((maxId, row) => Math.max(maxId, Number(row.id) || 0), 0) + 1;
                const newRows = returnedUnits.map((unit, index) => mapUnitToRow(unit, nextId + index));
                return [...prevRows, ...newRows];
            });
            setUnitPdfFile(null);
            setShowManualUnitForm(false);
        } else {
            setShowManualUnitForm(true);
        }

        setMessage(response?.message ?? (returnedUnits.length > 0 ? 'Unidade adicionada!' : 'Não foi possível inserir a unidade pelo PDF.'));
        setStatus(response?.status === 'success' && returnedUnits.length === 0 ? 'error' : response?.status ?? (returnedUnits.length > 0 ? 'success' : 'error'));
        setOpenAlerta(true);
        setIsInsertingUnit(false);
    };

    const addUnidades = async () => {
        const hasAllRequiredManualFields = requiredManualUnitFields.every((field) => (manualUnitForm[field] ?? '').toString().trim() !== '') &&
            requiredManualAddressFields.every((field) => (manualUnitForm.endereco?.[field] ?? '').toString().trim() !== '');

        if (!unitPdfFile) {
            setMessage('Envie um PDF para inserir a unidade.');
            setStatus('error');
            setOpenAlerta(true);
            return;
        }

        if (!hasAllRequiredManualFields) {
            setMessage('Preencha todos os campos da unidade!');
            setStatus('error');
            setOpenAlerta(true);
            setIsSubmit(false);
            return;
        }

        setIsInsertingUnit(true);
        const formData = new FormData();
        formData.append('contaEnergia', unitPdfFile);
        formData.append('nome', clientName);
        formData.append('cpf', cpfCnpj);
        formData.append('unit', JSON.stringify(buildManualUnitPayload(manualUnitForm)));
        if (crmLeadData?.lead_id) {
            formData.append('lead_id', String(crmLeadData.lead_id));
        }

        const response = await fetchFlask(CREATE_DOCUMENT_UNIT_PDF_API, formData);
        const returnedUnits = extractUnitsFromInsertResponse(response);

        if (response?.status === 'success') {
            setRows((prevRows) => {
                const nextId = prevRows.reduce((maxId, row) => Math.max(maxId, Number(row.id) || 0), 0) + 1;
                const newRows = returnedUnits.length > 0
                    ? returnedUnits.map((unit, index) => mapUnitToRow(unit, nextId + index))
                    : [mapManualUnitToRow(manualUnitForm, nextId)];
                return [...prevRows, ...newRows];
            });
            setUnitPdfFile(null);
            setShowManualUnitForm(false);
            setManualUnitForm(createManualUnitForm());
        }

        setMessage(response?.message ?? (response?.status === 'success' ? 'Unidade adicionada!' : 'Erro ao inserir unidade.'));
        setStatus(response?.status ?? 'error');
        setOpenAlerta(true);
        setIsInsertingUnit(false);
    };

    useEffect(() => {
        async function getCoupons () {
            const response = await fetchFlask('getValidCoupons');
            if (response.status !== 'error') {
                setCouponsList(response.coupons);
            } else {
                handleAlerta(response);
            }
        };
        getCoupons();
    }, []);

    async function submitSerasa(e) {
        e.preventDefault();
        setIsSubmit(true);

        const body = JSON.stringify({
            document: cpfCnpj,
            name: clientName
        });

        const response = await fetchFlask('checkSerasa', body);
        if (response.status === 'success') {
            setApproved(true);
            setClientType(cpfCnpj.length === 14 ? 'pf' : 'pj');
        }
        handleAlerta(response);
        setIsSubmit(false);
    };

    async function getAddressData(e) {
        e.preventDefault();
        setIsSubmit(true);

        const body = JSON.stringify({
            cep
        });

        const response = await fetchFlask('buscarDadosCEP', body);
        if (response.status === 'success') {
            setStreet(response.data.logradouro);
            setNeighborhood(response.data.bairro);
            setCity(response.data.localidade);
            if (response.data.estado === 'Pernambuco') {
                setState('PE');
            } else if (response.data.estado === 'Rio Grande do Norte') {
                setState('RN');
            } else {
                setState('');
            }
        } else {
            handleAlerta(response);
        }
        setIsSubmit(false);
    };

    async function submitCreateDocuments(e) {
        e.preventDefault();
        setIsSubmit(true);

        const formData = new FormData();

        const units = rows.map(row => {
            const unitData = {
                cc: row.cc,
                address: row.address,
                consumption: row.consumption,
                state: row.state
            };

            return unitData;
        });

        if (files.cnh) formData.append('cnh', files.cnh);
        if (files.contrato) formData.append('contrato', files.contrato);
        if (files.cnpj) formData.append('cnpj', files.cnpj);

        if (clientType === 'pf' && cpfCnpj.length !== 14) {
            setMessage('CPF com dígitos faltando!');
            setStatus('error');
            setOpenAlerta(true);
            setIsSubmit(false);
            return;
        }

        if (clientType === 'pj' && cpfCnpj.length !== 18) {
            setMessage('CNPJ com dígitos faltando!');
            setStatus('error');
            setOpenAlerta(true);
            setIsSubmit(false);
            return;
        }

        if (units.length === 0) {
            setMessage('Adicione no mínimo uma unidade!');
            setStatus('error');
            setOpenAlerta(true);
            setIsSubmit(false);
            return;
        }

        const representativeAddressParts = [representativeAddress, representativeCep].filter(Boolean);
        if (complement) {
            formData.append('complement', complement);
        }

        formData.append('representativeAddress', representativeAddressParts.join(', '));
        formData.append('clientType', clientType);
        formData.append('clientName', clientName);
        formData.append('cpfCnpj', cpfCnpj);
        formData.append('street', street);
        formData.append('addressNumber', number);
        formData.append('neighborhood', neighborhood);
        formData.append('city', city);
        formData.append('cep', cep);
        formData.append('state', state);
        formData.append('maritalStatus', maritalStatus);
        formData.append('representativeName', representativeName);
        formData.append('representativeCpf', representativeCpf);
        formData.append('discount', discount);
        formData.append('classification', classification);
        formData.append('verificationType', verificationType);
        formData.append('contactName', contactName);
        formData.append('email', contactEmail);
        formData.append('phone1', phone1);
        formData.append('phone2', phone2 === null || phone2 === undefined ? '' : phone2);
        formData.append('oppName', oppName);
        formData.append('units', JSON.stringify(units));
        formData.append('notificarCliente', notificarCliente);
        formData.append('coupon', coupon === 'Sem Cupom' ? '' : coupon);
        if (crmLeadData?.lead_id) {
            formData.append('lead_id', String(crmLeadData.lead_id));
        }

        const response = await fetchFlask('createDocuments', formData);
        handleAlerta(response);
        setIsSubmit(false);
    };

    async function submitCartaoCNpj(e) {
        e.preventDefault();
        setClientType('pj');
        setIsSubmit(true);
        const formData = new FormData();

        if (files.cnpj) {
            formData.append('cnpj', files.cnpj);
        } else {
            setMessage('Faça o upload do cartão CNPJ');
            setStatus('error');
            setOpenAlerta(true);
            return;
        }

        const response = await fetchFlask('getCnpjData', formData);
        if (response.status === 'success') {
            setClientName(response.name);
            setStreet(response.street);
            setNumber(response.number);
            setCity(response.city);
            setNeighborhood(response.neighborhood);
            setComplement(response.complement);
            setCpfCnpj(response.cnpj);
            setCep(response.cep);
        } else {
            handleAlerta(response);
        }
        setIsSubmit(false);
    }

    function handleAlerta(response) {
        setMessage(response.message);
        setStatus(response.status);
        setOpenAlerta(true);
    }

    return (
        <Box className='mainBox'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader>Criar contrato</PageHeader>

            {!approved && (<form action='' method='POST' id='formApproveSerasa' onSubmit={submitSerasa}>
                <Grid container spacing={1} justifyContent='center' alignItems='center'>
                    <Grid item xs={5}>
                        <Paper elevation={3} sx={{ padding: 2, marginTop: '20px' }}>
                            <Grid container justifyContent='center' marginBottom={2}>
                                <Typography variant='h4' marginBottom={2}>Consultar no Serasa</Typography>

                                <Grid container justifyContent='center' marginBottom={2}>
                                    <Grid item xs={8}>
                                        <FormControl sx={{ textAlign: 'center', display: 'flex' }}>
                                            <TextField
                                                label='Descrição da oportunidade'
                                                value={oppName}
                                                onChange={(e) => setOppName(e.target.value)}
                                                required
                                            />
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                <Grid item xs={8}>
                                    <FormControl sx={{ display: 'flex' }}>
                                        <TextField
                                            required
                                            label='CPF ou CNPJ'
                                            value={cpfCnpj}
                                            inputProps={{ maxLength: 18 }}
                                            onChange={(e) => setCpfCnpj(maskCpfCnpj(e.target.value))}
                                        />
                                    </FormControl>
                                </Grid>
                            </Grid>

                            <Grid container justifyContent="center">
                                <Grid item xs={6}>
                                    <FormControl sx={{ display: 'flex' }}>
                                        <SubmitButton isSubmit={isSubmit} >Pesquisa Serasa</SubmitButton>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>
            </form>)}

            {approved && (<form action='' method='POST' id='formNotificarCliente' onSubmit={submitCreateDocuments}>
                <Grid container>
                    <Grid item xs={5}>
                        <Paper elevation={3} sx={{ padding: 2, margin: '10px', marginTop: '20px' }}>
                            <Typography variant='h3' sx={{ marginBottom: '10px' }}>
                                Cliente
                            </Typography>

                            {clientType === 'pj' && (
                                <Grid container justifyContent="center">
                                    <Grid item xs={10}>
                                        <FormControl fullWidth>
                                            <Typography variant='h6'>Cartão CNPJ</Typography>
                                            <TextField
                                                type='file'
                                                size='small'
                                                inputProps={{ accept: '*/*' }}
                                                onChange={(e) => setFiles({ ...files, cnpj: e.target.files[0] })}
                                            />
                                        </FormControl>

                                        <Grid container justifyContent="center" marginTop='10px'>
                                            <Grid item>
                                                <Button
                                                    variant='contained'
                                                    disabled={isSubmit}
                                                    onClick={submitCartaoCNpj}
                                                    endIcon={isSubmit && <CircularProgress size={20} />}
                                                >
                                                    Buscar dados
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}

                            <Grid container spacing={1} sx={{ justifyContent: 'center' }}>
                                <Grid item xs={12}>
                                    <FormControl sx={{ display: 'flex', marginTop: '10px' }}>
                                        <TextField
                                            required
                                            label='Nome do Cliente'
                                            value={clientName}
                                            onChange={(e) => setClientName(e.target.value)}
                                            disabled
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid item xs={5}>
                                    <FormControl sx={{ display: 'flex', marginTop: '10px' }}>
                                        <TextField
                                            required
                                            label='CPF ou CNPJ'
                                            value={cpfCnpj}
                                            inputProps={{ maxLength: clientType === 'pf' ? 14 : 18 }}
                                            onChange={(e) => setCpfCnpj(maskCpfCnpj(e.target.value))}
                                            disabled
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid item xs={4}>
                                    <FormControl sx={{ display: 'flex', marginTop: '10px' }}>
                                        <InputLabel>Estado Civil</InputLabel>
                                        <Select
                                            value={maritalStatus}
                                            label="Estado Civil"
                                            onChange={(e) => setMaritalStatus(e.target.value)}
                                        // required
                                        >
                                            <MenuItem value="Casado">Casado</MenuItem>
                                            <MenuItem value="Solteiro">Solteiro</MenuItem>
                                            <MenuItem value="União Estável">União Estável</MenuItem>
                                            <MenuItem value="Divorciado">Divorciado</MenuItem>
                                            <MenuItem value="Viúvo">Viúvo</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={3}>
                                    <FormControl sx={{ display: 'flex', marginTop: '10px' }}>
                                        <TextField
                                            required
                                            label='Desconto'
                                            value={discount}
                                            disabled
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid item xs={4}>
                                    <FormControl sx={{ display: 'flex', marginTop: '10px' }}>
                                        <TextField
                                            required
                                            label='CEP'
                                            value={cep}
                                            inputProps={{ maxLength: 9 }}
                                            onChange={(e) => setCep(maskCEP(e.target.value))}
                                            disabled={clientType === 'pj'}
                                        />
                                    </FormControl>
                                </Grid>

                                {clientType === 'pf' && (
                                    <Grid container justifyContent="center">
                                        <Grid item xs={6}>
                                            <FormControl sx={{ display: 'flex', marginTop: '10px' }}>
                                                <Button variant='contained' onClick={getAddressData}>Buscar CEP</Button>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                )}

                                <Grid item xs={6}>
                                    <FormControl sx={{ display: 'flex', marginTop: '10px' }}>
                                        <TextField
                                            required
                                            label='Rua'
                                            value={street}
                                            onChange={(e) => setStreet(e.target.value)}
                                            disabled={clientType === 'pj'}
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid item xs={3}>
                                    <FormControl sx={{ display: 'flex', marginTop: '10px' }}>
                                        <TextField
                                            required
                                            label='Número'
                                            value={number}
                                            onChange={(e) => setNumber(e.target.value)}
                                            disabled={clientType === 'pj'}
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid item xs={3}>
                                    <FormControl sx={{ display: 'flex', marginTop: '10px' }}>
                                        <TextField
                                            label='Compl.'
                                            value={complement}
                                            onChange={(e) => setComplement(e.target.value)}
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid item xs={6}>
                                    <FormControl sx={{ display: 'flex', marginTop: '10px' }}>
                                        <TextField
                                            required
                                            label='Bairro'
                                            value={neighborhood}
                                            onChange={(e) => setNeighborhood(e.target.value)}
                                            disabled={clientType === 'pj'}
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid item xs={6}>
                                    <FormControl sx={{ display: 'flex', marginTop: '10px' }} fullWidth>
                                        <Autocomplete
                                            options={cidadesPorEstado[state] || []}
                                            value={city || null}
                                            onChange={(_, newValue) => setCity(newValue || '')}
                                            isOptionEqualToValue={(option, value) => option === value}
                                            noOptionsText={
                                                state
                                                ? 'Nenhuma cidade encontrada'
                                                : 'Escolha um estado para ver a lista de cidades'
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    required
                                                    label="Cidade"
                                                />
                                            )}
                                            disabled={clientType === 'pj'}
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid item xs={4}>
                                    <FormControl sx={{ display: 'flex', marginTop: '10px' }}>
                                        <InputLabel id='labelState'>Estado</InputLabel>
                                        <Select
                                            id='selectState'
                                            labelId='labelState'
                                            label='State'
                                            value={state}
                                            required
                                            onChange={(e) => {
                                                setState(e.target.value);
                                                if (clientType !== 'pj') {
                                                    setCity('');
                                                }
                                            }}
                                        >
                                            {(clientType === 'pj' ? ESTADOS_BRASIL : ESTADOS_ATENDIDOS).map((uf) => (
                                                <MenuItem key={uf} value={uf}>{uf}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={4}>
                                    <FormControl sx={{ display: 'flex', marginTop: '10px' }}>
                                        <InputLabel id='labelClassificacao'>Classificação</InputLabel>
                                        <Select
                                            id='selectClassificacao'
                                            labelId='labelClassificacao'
                                            label='Classificacao'
                                            value={classification}
                                            required
                                            onChange={(e) => setClassification(e.target.value)}
                                        >
                                            <MenuItem value={'bt'}>Baixa Tensão</MenuItem>
                                            <MenuItem value={'at'}>Alta Tensão</MenuItem>
                                            <MenuItem value={'bto'}>Baixa Tensão Optante</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            {clientType !== 'pf' && (
                                <>
                                    <Paper elevation={2} sx={{ padding: 2, marginTop: '20px' }}>
                                        <Typography variant='h5' sx={{ marginBottom: '10px' }}>
                                            Representante
                                        </Typography>

                                        <FormControl sx={{ display: 'flex', marginTop: '10px' }}>
                                            <TextField
                                                required
                                                label='Nome do Representante'
                                                value={representativeName}
                                                onChange={(e) => setRepresentativeName(e.target.value)}
                                            />
                                        </FormControl>

                                        <FormControl sx={{ display: 'block', marginTop: '10px' }}>
                                            <TextField
                                                required
                                                label='CPF do Representante'
                                                value={representativeCpf}
                                                inputProps={{ maxLength: 14 }}
                                                onChange={(e) => setRepresentativeCpf(maskCpfCnpj(e.target.value))}
                                            />
                                        </FormControl>

                                        <FormControl sx={{ display: 'flex', marginTop: '10px' }}>
                                            <TextField
                                                required
                                                label='Endereço do Representante'
                                                value={representativeAddress}
                                                onChange={(e) => setRepresentativeAddress(e.target.value)}
                                            />
                                        </FormControl>

                                        <FormControl sx={{ display: 'block', marginTop: '10px' }}>
                                            <TextField
                                                required
                                                label='CEP do Representante'
                                                value={representativeCep}
                                                inputProps={{ maxLength: 9 }}
                                                onChange={(e) => setRepresentativeCep(maskCEP(e.target.value))}
                                            />
                                        </FormControl>
                                    </Paper>
                                </>
                            )}

                            <Paper elevation={2} sx={{ padding: 2, marginTop: '20px' }}>
                                <Typography variant='h5'>
                                    Contato
                                </Typography>

                                <Grid container>
                                    <Grid item xs={7}>
                                        <FormControl sx={{ display: 'flex', marginTop: '10px' }}>
                                            <InputLabel id='labelClassificacao'>Token de Verificação</InputLabel>
                                            <Select
                                                id='selectToken'
                                                labelId='labelToken'
                                                label='Token de Verificação'
                                                value={verificationType}
                                                required
                                                onChange={(e) => setVerificationType(e.target.value)}
                                            >
                                                <MenuItem value={'SMS'}>SMS</MenuItem>
                                                <MenuItem value={'EMAIL'}>e-mail</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={10}>
                                        <FormControl sx={{ display: 'flex', marginTop: '10px' }}>
                                            <TextField
                                                required
                                                label='Nome'
                                                value={contactName}
                                                onChange={(e) => setContactName(e.target.value)}
                                            />
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={10}>
                                        <FormControl sx={{ display: 'flex', marginTop: '10px' }}>
                                            <TextField
                                                required
                                                type="email"
                                                label='Email'
                                                value={contactEmail}
                                                onChange={(e) => setContactEmail(e.target.value)}
                                            />
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControl sx={{ display: 'block', marginTop: '10px' }}>
                                            <TextField
                                                required
                                                label='Telefone 1'
                                                inputProps={{ maxLength: 15 }}
                                                value={phone1}
                                                onChange={(e) => setPhone1(maskPhone(e.target.value))}
                                            />
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControl sx={{ display: 'block', marginTop: '10px' }}>
                                            <TextField
                                                label='Telefone 2'
                                                inputProps={{ maxLength: 15 }}
                                                value={phone2}
                                                onChange={(e) => setPhone2(maskPhone(e.target.value))}
                                            />
                                        </FormControl>
                                    </Grid>

                                </Grid>
                            </Paper>
                        </Paper>
                    </Grid>

                    <Grid item xs={7}>
                        <Paper elevation={3} sx={{ padding: 2, margin: '20px' }}>
                            <Grid container alignItems="center" justifyContent="space-between" marginBottom={2}>
                                <Grid item>
                                    <Typography variant='h3' sx={{ marginBottom: '10px' }}>
                                        Unidades
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Paper elevation={1} sx={{ backgroundColor: '#f0f1f383', marginTop: '10px', width: '100%' }}>
                                <Box>
                                    <DataGrid
                                        rows={rows}
                                        columns={columns}
                                        processRowUpdate={processRowUpdate}
                                        onSelectionModelChange={handleRowSelection}
                                        localeText={{ noRowsLabel: 'Sem Unidades' }}
                                        hideFooter
                                        autoHeight
                                    />
                                </Box>
                                {selectedRows.length > 0 && (
                                    <Tooltip title="Delete">
                                        <IconButton onClick={() => deleteCC(selectedRows)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                <Grid container spacing={2} sx={{ padding: 2, justifyContent: 'center' }}>
                                    <Grid item xs={10}>
                                        <Typography variant='h6'>Inserir unidade por PDF</Typography>
                                    </Grid>

                                    <Grid item xs={8}>
                                        <FormControl fullWidth>
                                            <TextField
                                                type='file'
                                                inputProps={{ accept: 'application/pdf' }}
                                                onChange={(e) => {
                                                    setUnitPdfFile(e.target.files?.[0] ?? null);
                                                    setShowManualUnitForm(false);
                                                }}
                                            />
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Button
                                            variant='contained'
                                            onClick={insertUnitByPdf}
                                            disabled={isInsertingUnit}
                                            endIcon={isInsertingUnit && <CircularProgress size={20} />}
                                        >
                                            Inserir unidade
                                        </Button>
                                    </Grid>

                                    {showManualUnitForm && (
                                        <>
                                            <Grid item xs={10}>
                                                <Typography variant='h6'>Não foi possível inserir pelo PDF. Preencha manualmente a unidade.</Typography>
                                            </Grid>

                                    <Grid item>
                                        <FormControl fullWidth sx={{ marginBottom: '10px' }}>
                                            <TextField
                                                label='Titular'
                                                value={manualUnitForm.titular ?? ''}
                                                onChange={(e) => updateManualUnitField('titular', e.target.value)}
                                            />
                                        </FormControl>
                                    </Grid>

                                    <Grid item>
                                        <FormControl fullWidth sx={{ marginBottom: '10px' }}>
                                            <TextField
                                                label='Codigo Do Cliente'
                                                value={manualUnitForm.codigo_do_cliente ?? ''}
                                                onChange={(e) => updateManualUnitField('codigo_do_cliente', e.target.value)}
                                            />
                                        </FormControl>
                                    </Grid>

                                    <Grid item>
                                        <FormControl fullWidth sx={{ marginBottom: '10px' }}>
                                            <TextField
                                                label='Instalacao'
                                                value={manualUnitForm.instalacao ?? ''}
                                                onChange={(e) => updateManualUnitField('instalacao', e.target.value)}
                                            />
                                        </FormControl>
                                    </Grid>

                                    <Grid item>
                                        <FormControl fullWidth sx={{ marginBottom: '10px' }}>
                                                <InputLabel id='manual-address-estado-label'>Estado</InputLabel>
                                                <Select
                                                labelId='manual-address-estado-label'
                                                label='Estado'
                                                sx={{ width: '100px' }}
                                                value={manualUnitForm.endereco?.estado ?? ''}
                                                onChange={(e) => updateManualUnitAddressField('estado', e.target.value)}
                                                required
                                            >
                                                <MenuItem value='PE' >PE</MenuItem>
                                                <MenuItem value='RN' >RN</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item>
                                        <FormControl fullWidth>
                                            <TextField
                                                label="Consumo Medio"
                                                type="text"
                                                inputProps={{ inputMode: 'numeric' }}
                                                value={manualUnitForm.consumo_medio ?? ''}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (/^\d*$/.test(value)) {
                                                        updateManualUnitField('consumo_medio', value);
                                                    }
                                                }}
                                            />
                                        </FormControl>
                                    </Grid>

                                    <Grid item>
                                        <FormControlLabel
                                            control={(
                                                <Switch
                                                    checked={Boolean(manualUnitForm.alta_tensao)}
                                                    onChange={(e) => updateManualUnitField('alta_tensao', e.target.checked)}
                                                />
                                            )}
                                            label='Alta tensao'
                                        />
                                    </Grid>

                                    <Grid item xs={10}>
                                        <FormControl fullWidth>
                                            <TextField
                                                label='Logradouro'
                                                required
                                                value={manualUnitForm.endereco?.rua ?? ''}
                                                onChange={(e) => updateManualUnitAddressField('rua', e.target.value)}
                                            />
                                        </FormControl>
                                    </Grid>

                                    <Grid item>
                                        <FormControl fullWidth>
                                            <TextField
                                                label='Numero'
                                                required
                                                value={manualUnitForm.endereco?.numero ?? ''}
                                                onChange={(e) => updateManualUnitAddressField('numero', e.target.value)}
                                            />
                                        </FormControl>
                                    </Grid>

                                    <Grid item>
                                        <FormControl fullWidth>
                                            <TextField
                                                label='Complemento'
                                                value={manualUnitForm.endereco?.complemento ?? ''}
                                                onChange={(e) => updateManualUnitAddressField('complemento', e.target.value)}
                                            />
                                        </FormControl>
                                    </Grid>

                                    <Grid item>
                                        <FormControl fullWidth>
                                            <TextField
                                                label='Bairro'
                                                required
                                                value={manualUnitForm.endereco?.bairro ?? ''}
                                                onChange={(e) => updateManualUnitAddressField('bairro', e.target.value)}
                                            />
                                        </FormControl>
                                    </Grid>

                                    <Grid item>
                                        <FormControl fullWidth>
                                            <TextField
                                                label='Cidade'
                                                required
                                                value={manualUnitForm.endereco?.cidade ?? ''}
                                                onChange={(e) => updateManualUnitAddressField('cidade', e.target.value)}
                                            />
                                        </FormControl>
                                    </Grid>

                                    <Grid item>
                                        <FormControl fullWidth>
                                            <TextField
                                                label='Cep'
                                                required
                                                value={manualUnitForm.endereco?.cep ?? ''}
                                                onChange={(e) => updateManualUnitAddressField('cep', e.target.value)}
                                            />
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Button
                                            variant='contained'
                                            onClick={addUnidades}
                                            disabled={isInsertingUnit}
                                            endIcon={isInsertingUnit && <CircularProgress size={20} />}
                                        >
                                            Adicionar Unidade
                                        </Button>
                                    </Grid>
                                        </>
                                    )}
                                </Grid>
                            </Paper>

                            <Paper elevation={2} sx={{ padding: 2, marginTop: '20px' }}>
                                <Typography variant='h5'>
                                    Documentos
                                </Typography>
                                <Grid container spacing={2} sx={{ padding: 2, justifyContent: 'center' }}>
                                    <Grid item xs={6}>
                                        <FormControl sx={{ display: 'block', marginBottom: '10px' }}>
                                            <Typography variant='h6' sx={{ textAlign: 'center' }}>CNH</Typography>
                                            <TextField
                                                type='file'
                                                size='small'
                                                inputProps={{ accept: '*/*' }}
                                                onChange={(e) => setFiles({ ...files, cnh: e.target.files[0] })}
                                            />
                                        </FormControl>

                                        {clientType !== 'pf' && (
                                            <>
                                                <FormControl sx={{ display: 'block', marginBottom: '10px' }}>
                                                    <Typography variant='h6' sx={{ textAlign: 'center' }}>Contrato Social</Typography>
                                                    <TextField
                                                        type='file'
                                                        size='small'
                                                        inputProps={{ accept: '*/*' }}
                                                        onChange={(e) => setFiles({ ...files, contrato: e.target.files[0] })}
                                                    />
                                                </FormControl>

                                            </>
                                        )}
                                    </Grid>
                                </Grid>
                            </Paper>

                            {canChooseClientNotification && (
                                <Paper elevation={2} sx={{ padding: 2, marginTop: '20px' }}>
                                    <Grid container spacing={2} sx={{ padding: 2, justifyContent: 'center' }}>
                                        <Typography variant='h5'>
                                            Notificacao do cliente
                                        </Typography>

                                        <Grid item xs={12}>
                                            <FormControlLabel label={notificarCliente ? 'Notificar cliente' : 'Nao notificar cliente'}
                                                control={
                                                    <Switch
                                                        onChange={() => { setNotificarCliente(!notificarCliente); }}
                                                        checked={notificarCliente}
                                                    />
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Paper>
                            )}

                            <Grid container spacing={2} sx={{ padding: 2, justifyContent: 'center', marginTop: '25px', marginBottom: '25px' }}>
                                <Grid item xs={6}>
                                    <FormControl sx={{ display: 'flex' }}>
                                        <InputLabel id='labelCupom'>Cupom</InputLabel>
                                        <Select
                                            id='selectCupom'
                                            labelId='labelCupom'
                                            label='Cupom'
                                            value={coupon}
                                            required
                                            onChange={(e) => setCoupon(e.target.value)}
                                        >
                                            <MenuItem value={'Sem Cupom'}>Sem Cupom</MenuItem>
                                            {selectableCoupons.map((item) => (
                                                <MenuItem key={item} value={item}>{item}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            <Grid container spacing={2} sx={{ padding: 2, justifyContent: 'center', marginTop: '50px', marginBottom: '50px' }}>
                                <Grid item xs={9}>
                                    <FormControl sx={{ display: 'flex' }}>
                                        <SubmitButton isSubmit={isSubmit} >Criar Documentos</SubmitButton>
                                    </FormControl>
                                </Grid>
                            </Grid>

                        </Paper>
                    </Grid>
                </Grid>
            </form>)}
        </Box>
    );
};

export default createDocuments;
